/*
 * Desktop visual menu: hover/click panels under the header bar.
 * Markup: snippets/header-visual-menu.liquid (bar) + snippets/header-visual-menu-panels.liquid (layer).
 */
class VisualMenu extends HTMLElement {
  constructor() {
    super();
    this.openId = null;
    this.closeTimer = 0;
    this.panels = new Map();
    this.onItemEnter = this.onItemEnter.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onLayerClick = this.onLayerClick.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
    this.cancelClose = this.cancelClose.bind(this);
    this.scheduleClose = this.scheduleClose.bind(this);
    this.preloadImages = this.preloadImages.bind(this);
  }

  connectedCallback() {
    this.layer = document.getElementById(this.dataset.panels);
    if (!this.layer) return;
    this.overlay = this.layer.querySelector('.visual-menu__overlay');
    this.items = [...this.querySelectorAll('[data-panel]')];
    this.items.forEach((item) => {
      const panel = document.getElementById(item.dataset.panel);
      if (!panel) return;
      this.panels.set(item.dataset.panel, { item, panel });
      item.addEventListener('pointerenter', this.onItemEnter);
      item.addEventListener('click', this.onItemClick);
    });

    this.addEventListener('pointerenter', this.cancelClose);
    this.addEventListener('pointerenter', this.preloadImages, { once: true });
    this.addEventListener('focusin', this.preloadImages, { once: true });
    this.addEventListener('pointerleave', this.scheduleClose);
    this.addEventListener('focusout', this.onFocusOut);
    this.layer.addEventListener('pointerenter', this.cancelClose);
    this.layer.addEventListener('pointerleave', this.scheduleClose);
    this.layer.addEventListener('click', this.onLayerClick);
    this.layer.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('pointerdown', this.onPointerDown);

    this.panels.forEach(({ panel }) => this.bindBrandTabs(panel));

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.cancelClose();
  }

  /* ------------------------------------------------------------ open / close */

  setOpen(id) {
    if (this.openId === id) return;
    if (this.openId) {
      const { item, panel } = this.panels.get(this.openId);
      panel.setAttribute('hidden', '');
      item.setAttribute('aria-expanded', 'false');
    }
    this.openId = id;
    this.layer.classList.toggle('visual-menu__layer--open', Boolean(id));
    if (!id) return;
    const { item, panel } = this.panels.get(id);
    panel.removeAttribute('hidden');
    item.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.setOpen(null);
  }

  // Panel images are lazy and hidden until a panel opens: start fetching as soon as the bar gets attention
  preloadImages() {
    this.layer.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  cancelClose() {
    window.clearTimeout(this.closeTimer);
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = window.setTimeout(() => this.setOpen(null), 150);
  }

  onItemEnter(event) {
    this.cancelClose();
    // A tap fires pointerenter and click back to back: let the click toggle on touch
    if (event.pointerType === 'touch') return;
    this.setOpen(event.currentTarget.dataset.panel);
  }

  onItemClick(event) {
    this.cancelClose();
    const id = event.currentTarget.dataset.panel;
    this.setOpen(this.openId === id ? null : id);
  }

  onLayerClick(event) {
    if (event.target === this.overlay) this.setOpen(null);
  }

  onKeyDown(event) {
    if (!this.openId) return;
    const { item, panel } = this.panels.get(this.openId);
    if (event.key === 'Escape') {
      this.setOpen(null);
      item.focus();
      return;
    }
    // The layer sits after the header icons in the DOM: bridge Tab between the open item and its panel
    if (event.key !== 'Tab') return;
    const focusable = panel.querySelectorAll('a[href], button:not([disabled])');
    if (!focusable.length) return;
    if (!event.shiftKey && document.activeElement === item) {
      event.preventDefault();
      focusable[0].focus();
    } else if (event.shiftKey && document.activeElement === focusable[0]) {
      event.preventDefault();
      item.focus();
    }
  }

  onPointerDown(event) {
    if (!this.openId) return;
    if (this.contains(event.target) || this.layer.contains(event.target)) return;
    this.setOpen(null);
  }

  onFocusOut(event) {
    if (!this.openId || !event.relatedTarget) return;
    if (this.contains(event.relatedTarget) || this.layer.contains(event.relatedTarget)) return;
    this.setOpen(null);
  }

  /* ------------------------------------------------------------ brand tabs (left column switches the model columns) */

  bindBrandTabs(panel) {
    const brands = [...panel.querySelectorAll('.visual-menu__brand')];
    if (!brands.length) return;
    const views = [...panel.querySelectorAll('.visual-menu__view')];
    const shortcuts = panel.querySelector('.visual-menu__shortcuts');
    const select = (view) => {
      brands.forEach((brand) => {
        const active = brand.dataset.view === view;
        brand.classList.toggle('visual-menu__brand--active', active);
        brand.setAttribute('aria-selected', String(active));
      });
      views.forEach((element) => {
        if (element.dataset.view === view) element.removeAttribute('hidden');
        else element.setAttribute('hidden', '');
      });
      // Shortcuts belong to the first brand only
      if (shortcuts) shortcuts.hidden = view !== brands[0].dataset.view;
    };
    brands.forEach((brand) => {
      const onSelect = () => select(brand.dataset.view);
      brand.addEventListener('pointerenter', onSelect);
      brand.addEventListener('focus', onSelect);
      brand.addEventListener('click', onSelect);
    });
  }

  /* ------------------------------------------------------------ theme editor */

  onBlockSelect(event) {
    const panel = event.target.closest('.visual-menu__panel');
    if (panel && this.panels.has(panel.id)) this.setOpen(panel.id);
  }

  onBlockDeselect(event) {
    const panel = event.target.closest('.visual-menu__panel');
    if (panel && this.openId === panel.id) this.setOpen(null);
  }
}

customElements.define('visual-menu', VisualMenu);
